/**
 * @file Ecran Renaissance : rubis a gagner (x1 / x3 quotidien), confirmation, achats des 3 bonus permanents en rubis.
 */
import type { RebirthStat } from '@core/index';
import { Decimal } from '@shared/bignum';
import type { TranslationKey } from '@i18n/index';
import { formatBig, formatMult } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { closeScreen, confirmAsk, send } from '../core/feedback';
import { attachHoldRepeat } from '../core/holdRepeat';
import { currencyIcon } from '../core/icons';
import { sheetHeader } from '../core/widgets';
import type { UiContext } from '../UiContext';

const STATS: ReadonlyArray<{ stat: RebirthStat; label: TranslationKey }> = [
  { stat: 'damage', label: 'rebirth.bonus.damage' },
  { stat: 'attackSpeed', label: 'rebirth.bonus.attackSpeed' },
  { stat: 'money', label: 'rebirth.bonus.money' },
];

interface BonusRow {
  level: HTMLElement;
  mult: HTMLElement;
  cost: HTMLElement;
  buy: HTMLElement;
  max: HTMLElement;
}

export class RebirthScreen extends Component {
  private gain!: HTMLElement;
  private count!: HTMLElement;
  private balance!: HTMLElement;
  private notYet!: HTMLElement;
  private doBtn!: HTMLElement;
  private doX3Btn!: HTMLElement;
  private readonly rows = new Map<RebirthStat, BonusRow>();

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen rebirth');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    const body = h('div', { class: 'sheet-body' });

    this.count = h('span', { class: 'rebirth-count' });
    this.balance = h('b', { class: 'rebirth-balance' });
    this.gain = h('div', { class: 'rebirth-gain' });
    this.notYet = h('p', { class: 'hint is-hidden' }, t.t('rebirth.notYet'));
    this.doBtn = h('button', { class: 'btn primary big', type: 'button', 'data-ui-block': true, onclick: () => void this.perform(false) }, t.t('rebirth.do'));
    this.doX3Btn = h('button', { class: 'btn gem big is-hidden', type: 'button', 'data-ui-block': true, onclick: () => void this.perform(true) }, t.t('rebirth.doX3'));

    body.append(
      h('p', { class: 'rebirth-desc' }, t.t('rebirth.desc')),
      h('div', { class: 'panel rebirth-box' }, h('div', { class: 'rebirth-top' }, this.count, h('span', { class: 'chip cur-ruby' }, currencyIcon('ruby'), this.balance)), this.gain, this.notYet, h('div', { class: 'btn-row column' }, this.doBtn, this.doX3Btn)),
    );

    const list = h('div', { class: 'bonus-list' });
    for (const { stat, label } of STATS) {
      const level = h('span', { class: 'bonus-level' });
      const mult = h('b', { class: 'bonus-mult' });
      const cost = h('b', { class: 'bonus-cost' });
      const buy = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true }, '+1');
      const max = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'rebirth.upgrade', stat, count: 'max' }) }, t.t('common.max'));
      // +1 maintenu = achats repetes acceleres (meme mecanique que les upgrades).
      this.addDisposer(
        attachHoldRepeat(buy, {
          startMs: this.ctx.config.balance.upgrades.holdRepeatMs.start,
          minMs: this.ctx.config.balance.upgrades.holdRepeatMs.min,
          accel: this.ctx.config.balance.upgrades.holdRepeatMs.accel,
          onFire: () => send(this.ctx, { type: 'rebirth.upgrade', stat, count: 1 }).ok,
        }),
      );
      this.rows.set(stat, { level, mult, cost, buy, max });
      list.append(
        h(
          'div',
          { class: 'bonus-row panel' },
          h('div', { class: 'bonus-main' }, h('div', { class: 'bonus-name' }, t.t(label)), h('div', { class: 'bonus-line' }, level, mult)),
          h('div', { class: 'bonus-buy' }, h('span', { class: 'chip cur-ruby' }, currencyIcon('ruby'), cost), h('div', { class: 'btn-row' }, buy, max)),
        ),
      );
    }
    body.append(list);
    this.el.append(sheetHeader(this.ctx, t.t('rebirth.title')), body);
    this.update(0);
  }

  private async perform(useDailyBonus: boolean): Promise<void> {
    if (!(await confirmAsk('rebirth.confirm'))) return;
    const r = send(this.ctx, { type: 'rebirth.perform', useDailyBonus });
    if (r.ok) closeScreen();
  }

  override update(_dt: number): void {
    const { query, data } = this.ctx.sim;
    const { i18n } = this.ctx;
    const rb = query.rebirth();
    setText(this.balance, formatBig(data.wallet.ruby));
    setText(this.count, i18n.t('rebirth.count', { n: data.rebirth.count }));
    setText(this.gain, i18n.t('rebirth.gain', { rubies: formatBig(rb.rubies) }));
    setText(this.doX3Btn, `${i18n.t('rebirth.doX3')} (+${formatBig(rb.rubiesX3)})`);
    toggleClass(this.notYet, 'is-hidden', rb.can);
    toggleClass(this.doBtn, 'disabled', !rb.can);
    toggleClass(this.doX3Btn, 'is-hidden', !(rb.dailyAvailable && rb.can));
    toggleClass(this.doX3Btn, 'disabled', !rb.can);
    const per = this.ctx.config.balance.rebirth.bonusPerLevel;
    for (const [stat, row] of this.rows) {
      const lv = data.rebirth.levels[stat];
      const cost = rb.costs[stat];
      setText(row.level, i18n.t('common.level', { level: lv }));
      setText(row.mult, formatMult(Decimal.pow(per, lv)));
      setText(row.cost, formatBig(cost));
      const poor = data.wallet.ruby.lt(cost);
      toggleClass(row.buy, 'disabled', poor);
      toggleClass(row.max, 'disabled', poor);
    }
  }
}
